import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './LegalPage.css';

const TermsOfServicePage = () => {
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      <Helmet>
        <title>Terms of Service | coparent</title>
        <meta name="description" content="The terms that govern your use of coparent — the shared calendar, expenses, and custody schedule app for two-home families." />
        <link rel="canonical" href="https://letscoparent.us/terms" />
        <meta property="og:title" content="Terms of Service | coparent" />
        <meta property="og:description" content="The terms that govern your use of coparent." />
        <meta property="og:url" content="https://letscoparent.us/terms" />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="legal-nav">
        <button className="legal-back" onClick={() => navigate('/')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
          Back to home
        </button>
      </div>

      <div className="legal-container">
        <h1 className="legal-title">Terms of Service</h1>
        <p className="legal-updated">Last updated: March 27, 2026</p>

        <div className="legal-content">
          <section>
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing or using the coparent web application and related services (the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service.
            </p>
            <p>
              We may update these Terms from time to time. Continued use of the Service after changes constitutes acceptance of the revised Terms.
            </p>
          </section>

          <section>
            <h2>2. Description of Service</h2>
            <p>
              coparent is a web-based platform that helps co-parents coordinate custody schedules, share expenses, manage children's activities, communicate, and optionally sync calendar data to Google Calendar. The Service is provided "as is" and is intended to facilitate co-parenting logistics.
            </p>
          </section>

          <section>
            <h2>3. Account Registration</h2>
            <p>To use the Service, you must create an account using an email address and password or by signing in with Google. You agree to:</p>
            <ul>
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain the security of your account credentials</li>
              <li>Promptly notify us of any unauthorized use of your account</li>
              <li>Accept responsibility for all activity that occurs under your account</li>
            </ul>
          </section>

          <section>
            <h2>4. Acceptable Use</h2>
            <p>You agree to use the Service only for its intended purpose of co-parenting coordination. You may not:</p>
            <ul>
              <li>Use the Service to harass, intimidate, or threaten your co-parent or any other person</li>
              <li>Submit false, misleading, or fraudulent information</li>
              <li>Attempt to gain unauthorized access to the Service or other users' accounts</li>
              <li>Use the Service for any illegal purpose or in violation of any applicable laws</li>
              <li>Interfere with or disrupt the integrity or performance of the Service</li>
              <li>Reverse engineer, decompile, or disassemble any portion of the Service</li>
            </ul>
          </section>

          <section>
            <h2>5. Family Groups and Shared Data</h2>
            <p>
              When you create or join a family group, certain data — including custody schedules, expenses, activities, and messages — is shared with other members of that family group. By using the Service, you consent to this sharing of information with your co-parent and any household members added to the family group.
            </p>
            <p>
              You are responsible for the accuracy of the information you submit. We are not responsible for disputes between co-parents arising from the use of the Service.
            </p>
          </section>

          <section>
            <h2>6. Google Calendar Integration</h2>
            <p>
              The Service offers optional integration with Google Calendar. By connecting your Google account, you authorize coparent to create and manage events in a dedicated calendar within your Google account. You may disconnect this integration at any time through the Service's settings.
            </p>
            <p>
              We are not responsible for any issues arising from changes to Google's APIs, service outages, or modifications to your Google Calendar data made outside of the Service.
            </p>
          </section>

          <section>
            <h2>7. Intellectual Property</h2>
            <p>
              The Service, including its design, features, content, and code, is owned by coparent and is protected by intellectual property laws. You are granted a limited, non-exclusive, non-transferable license to use the Service for your personal, non-commercial co-parenting needs.
            </p>
            <p>
              You retain ownership of any content you submit to the Service (such as messages and notes). By submitting content, you grant us a license to use, store, and display it as necessary to provide the Service.
            </p>
          </section>

          <section>
            <h2>8. Privacy</h2>
            <p>
              Your use of the Service is also governed by our <a href="/privacy" style={{ color: '#1B6B4A', fontWeight: 600 }}>Privacy Policy</a>, which explains how we collect, use, and protect your information. By using the Service, you consent to the data practices described in the Privacy Policy.
            </p>
          </section>

          <section>
            <h2>9. Disclaimer of Warranties</h2>
            <p>
              The Service is provided on an "as is" and "as available" basis without warranties of any kind, either express or implied. We do not warrant that the Service will be uninterrupted, error-free, or secure. We do not guarantee the accuracy or completeness of any information provided through the Service.
            </p>
            <p>
              <strong>coparent is not a substitute for legal advice.</strong> The Service is a coordination tool and does not constitute legal, custody, or mediation services. Always consult a qualified professional for legal matters related to custody arrangements.
            </p>
          </section>

          <section>
            <h2>10. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law, coparent and its owners, employees, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service, including but not limited to loss of data, loss of profits, or damages resulting from co-parenting disputes.
            </p>
            <p>
              Our total liability for any claim arising from the Service shall not exceed the amount you paid us (if any) in the twelve months preceding the claim.
            </p>
          </section>

          <section>
            <h2>11. Termination</h2>
            <p>
              We may suspend or terminate your access to the Service at any time, with or without cause, and with or without notice. You may stop using the Service at any time by deleting your account. Upon termination, your right to use the Service ceases immediately.
            </p>
          </section>

          <section>
            <h2>12. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law principles. Any disputes arising from these Terms or the Service shall be resolved in the courts of competent jurisdiction.
            </p>
          </section>

          <section>
            <h2>13. Contact Us</h2>
            <p>If you have questions about these Terms, please contact us at:</p>
            <p><strong>Email:</strong> <a href="mailto:feedback@letscoparent.us">feedback@letscoparent.us</a></p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
